import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import Navigation from '../components/Navigation';
import SiteFooter from '../components/SiteFooter';
import SEO from '../components/SEO';
import { API } from '../config';

const ICON_MAP = {
  Bus: '🚌', CreditCard: '💳', Eye: '👁️', Users: '👨‍👩‍👧‍👦',
  GraduationCap: '🎓', Tag: '🏷️', Heart: '❤️', Shield: '🛡️',
  PiggyBank: '💰', TrendingUp: '📈', Calendar: '📅', Dumbbell: '🏋️',
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NewsPage() {
  const [newsData, setNewsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch(`${API}/news/by-category?limit=6`);
        const data = await res.json();
        setNewsData(data.categories || {});
      } catch (e) {
        console.error('Failed to fetch news:', e);
      }
      setLoading(false);
    };
    fetchNews();
  }, []);

  const toggleCategory = (slug) => {
    setExpandedCats(prev => ({ ...prev, [slug]: !prev[slug] }));
  };

  const categories = Object.entries(newsData);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <SEO
        title="Benefits News & Updates"
        description="Latest news on employee benefits, 401k, health insurance, credit cards, and workplace perks from trusted industry sources."
        path="/news"
        keywords="employee benefits news, 401k news, health insurance updates, credit card benefits, workplace benefits, HR news"
      />
      <Navigation />

      {/* Hero */}
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Benefits News & Updates</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Stay current with the latest in employee benefits, retirement, healthcare, and financial wellness.
          </p>
        </div>
      </section>

      {/* News Content */}
      <main className="flex-grow py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading latest news...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No news articles yet. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-12">
              {categories.map(([slug, catData]) => {
                const isExpanded = expandedCats[slug];
                const visibleArticles = isExpanded ? catData.articles : catData.articles.slice(0, 3);
                const hasMore = catData.articles.length > 3;

                return (
                  <section key={slug}>
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-2xl">{ICON_MAP[catData.icon] || '📄'}</span>
                      <h2 className="text-2xl font-bold text-gray-900">{catData.name}</h2>
                      <span className="text-sm text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        {catData.articles.length} articles
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {visibleArticles.map(article => (
                        
                          <a
                          key={article.id}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-blue-300 transition-all group overflow-hidden"
                        >
                          <div className="p-5">
                            <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                              <span>{article.source_icon}</span>
                              <span className="font-medium">{article.source}</span>
                              <span>·</span>
                              <Clock size={12} />
                              <span>{timeAgo(article.published_at)}</span>
                            </div>
                            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2 line-clamp-2">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-3">
                              {article.summary}
                            </p>
                            <div className="mt-3 flex items-center gap-1 text-xs text-blue-600 font-medium">
                              Read more <ExternalLink size={12} />
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>

                    {hasMore && (
                      <button
                        onClick={() => toggleCategory(slug)}
                        className="mt-4 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium mx-auto"
                      >
                        {isExpanded ? (
                          <>Show less <ChevronUp size={16} /></>
                        ) : (
                          <>Show all {catData.articles.length} articles <ChevronDown size={16} /></>
                        )}
                      </button>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
